import { FC, Fragment, useEffect } from 'react'
import { Tiny2 } from '@/components/atoms/typography'
import { Flex } from '@/components/atoms/Grid'
import styled from 'styled-components'
import { transparentize } from 'polished'
import Link from '@/components/atoms/link'
import { Routes } from '@/core/routes'
import { SlugPathItem } from 'types/slugPath'
import { useMainNavigationDispatch } from '@/providers/mainNavigationMenuProvider'
import { HomeIconDefault } from '@/components/atoms/icons/HomeIconDefault'

type CategoryPathHeaderProps = {
  paths: SlugPathItem[]
  showHome?: boolean
} & Rest

const CategoryPathHeader: FC<CategoryPathHeaderProps> = ({
  paths,
  showHome = true,
  ...rest
}) => {
  const { updateSlugPath } = useMainNavigationDispatch()

  useEffect(() => {
    updateSlugPath(paths)
    return () => {
      updateSlugPath([])
    }
  }, [paths])

  return (
    <>
      {paths?.length > 0 && (
        <Wrapper
          flexWrap="wrap"
          alignItems="center"
          {...rest}
          data-testid="categoryPathHeader"
        >
          {showHome && (
            <>
              <Link href={Routes.home}>
                <StyledBox>
                  <HomeIconDefault width={12} />
                </StyledBox>
              </Link>
              {paths.length > 0 && <Delimiter />}
            </>
          )}
          {paths
            .filter((item) => item)
            .map(({ url, title }, offset) => (
              <Fragment key={url + title}>
                <Link href={url}>
                  <PathItem key={url}>{title} </PathItem>
                </Link>
                {offset + 1 < paths.length && <Delimiter />}
              </Fragment>
            ))}
        </Wrapper>
      )}
    </>
  )
}

export default CategoryPathHeader

const Delimiter: FC = () => (
  <Tiny2 m={0} px={2} opacity={0.6}>
    &gt;
  </Tiny2>
)

const Wrapper = styled(Flex)`
  min-height: 38px;
  padding: 6px 12px;
  background-color: ${(props) =>
    transparentize(0.5, props.theme.colors.menuItemBackgroundSecondary)};
  border-radius: ${(props) => props.theme.borderRadius}px;
`

const PathItem = styled(Tiny2)`
  margin: 0;
  flex-direction: row;
  opacity: 0.6;
  &:hover {
    text-decoration: underline;
    cursor: pointer;
  }
`

const StyledBox = styled.div`
  margin: auto;
`
